import { Accordion, AccordionHeader, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';
import { DistributorsSelector, Flex, Typography } from 'front-commons/ds';
import HeadingTags from 'front-commons/ds/components/ProductCard/components/HeadingTags';
import { PromotionLimitatorInfo } from 'front-commons/ds/components/ProductCard/components/PromotionLimitatorInfo';
import { getTags } from 'hooks/useCatalogProductRules/helpers';
import { useCallback, useEffect, useState } from 'react';
import { PromotionDetailsDescriptionProps } from './interfaces';
import { SeeMoreContent, SeeMoreTrigger } from './styles';
import { Distributor } from 'front-commons/entities/Product/Distributor';

export function PromotionDetailsDescription({
	data,
	disableDistributorSelector,
	distributorWaiting,
	initialDistributor,
	recoveryLastDistributor,
	onChange,
}: PromotionDetailsDescriptionProps) {
	const [open, setOpen] = useState(true);
	const [value, setValue] = useState(initialDistributor || undefined);

	const handleOpen = () => {
		setOpen(!open);
	};

	const handleChange = useCallback(
		(selected: Partial<Distributor>) => {
			setValue(selected);
			onChange(selected, true);
		},
		[onChange],
	);

	useEffect(() => {
		const firstAvailableDistributor =
			initialDistributor || data.distributors.find((distributor) => distributor.status !== 'UNAVAILABLE');

		handleChange(firstAvailableDistributor || data.distributors[0]);
	}, []);

	return (
		<Accordion collapsible defaultValue="see-details" type="single">
			<AccordionItem value="see-details">
				<AccordionHeader>
					<Flex gap="0.5rem" direction="column">
						<Flex justifyContent="space-between" alignItems="center">
							<HeadingTags tags={getTags(data)} />

							<AccordionTrigger asChild>
								<SeeMoreTrigger
									onClick={handleOpen}
									open={open}
									rightIcon={{
										color: '--text-link',
										name: 'keyboard_arrow_up',
										size: '16px',
									}}
									variant="text"
								>
									{`Ver ${open ? 'menos' : 'mais'}`}
								</SeeMoreTrigger>
							</AccordionTrigger>
						</Flex>

						<Flex gap="0" direction="column">
							<Typography variant="Paragraph/Semibold">{data.description}</Typography>

							<Flex gap="0.5rem" alignItems="center">
								<Typography variant="Paragraph/XS" color="--text-secondary">
									{data.hyperaCode?.toUpperCase()}
								</Typography>
								{/* TODO: turn into a link redirecting to a page with only hypera products once the url/page is created */}
								{/* <Typography variant="Label/XS" color="--text-link">
									Hypera
								</Typography> */}
							</Flex>
						</Flex>
					</Flex>
				</AccordionHeader>

				<SeeMoreContent>
					<Flex margin="1rem 0 0 0" gap="1rem" direction="column">
						<Typography variant="ParagraphSmall/Regular">{data.moreAbout}</Typography>

						<PromotionLimitatorInfo
							limit={data.availableLimit}
							limitedByQuantity={!!data.limitedByQuantity}
							reached={!!data.limitedByQuantity && !data.availableLimit}
							exhibition="default"
						/>

						<DistributorsSelector
							data={data.distributors}
							disabled={disableDistributorSelector}
							initialValue={value}
							onChange={onChange}
							recoveryLastDistributor={recoveryLastDistributor}
							waiting={distributorWaiting}
							showPrice={false}
							showStockLabel={false}
							watchInitialValue
							sideOffset={0}
						/>
					</Flex>
				</SeeMoreContent>
			</AccordionItem>
		</Accordion>
	);
}
